import { useCallback } from 'react'
import Icon from '../components/Icon'
import { useJobStatus } from '../hooks/useJobStatus'
import { fetchInsightRunStatus, launchInsightRun } from '../api/insights'
import styles from './InsightRunControl.module.css'

/**
 * InsightRunControl — the superadmin "Run now" button on one saved Control Panel
 * slot (AdminInsightsTab). Page-local: it wires this insight's run endpoints into
 * the shared useJobStatus machine, one instance per slot (a hook can't be called in
 * the page's loop).
 *
 *   none / done → "Run now"    launches a run (done = a previous run finished)
 *   generating  → "Running…"   spinner, inert while polling — also shown while a
 *                              scheduled run is in flight, which a manual run yields to
 *   error       → "Run again"  with the failure message beneath
 *
 * The backend refuses a launch while any run is in flight (409); that's not a
 * failure from the button's point of view — a run IS going — so it settles to
 * 'generating' and the poll follows that run instead.
 */
export default function InsightRunControl({ jobKey, title }) {
  const fetchStatus = useCallback(() => fetchInsightRunStatus(jobKey), [jobKey])
  const launch = useCallback(async () => {
    try {
      await launchInsightRun(jobKey)
    } catch (e) {
      if (e.status !== 409) throw e
    }
  }, [jobKey])

  const { status, error, start } = useJobStatus({
    identity: jobKey,
    fetchStatus,
    launch,
    failedMessage: 'The insight run failed.',
    launchFailedMessage: 'Could not start the insight run.',
  })

  const running = status === 'generating'
  const inert = running || status === 'unknown'
  const label = running ? 'Running…' : status === 'error' ? 'Run again' : 'Run now'

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={styles.runBtn}
        onClick={start}
        disabled={inert}
        aria-busy={running}
        aria-live="polite"
        aria-label={`${label} — ${title}`}
      >
        <Icon name={running ? 'spinner' : 'refresh'} spin={running} />
        {label}
      </button>
      {error && <span className={styles.error} role="alert">{error}</span>}
    </div>
  )
}
